import {useQuery} from '@apollo/client';
import {useCallback} from 'react';

import {PREDICTION_DETAIL_QUERY} from '../ModelHubQueries';
import type {PredictionJobStatus} from '../prediction-jobs/PredictionJobTypes';
import {STATUS_CODE_TO_STATUS} from '../runs/PredictionBatchTypes';

export interface PredictionDetail {
  id: string;
  name: string;
  createdAt: string;
  modifiedAt: string;
  modelName: string;
  status: PredictionJobStatus;
  componentApiIds: ReadonlyArray<string>;
  configJson: Record<string, unknown>;
  hasResultsFile: boolean;
  resultsDownloadUrl: string | null;
  standardizedOutputFileName: string | null;
  standardizedOutputDownloadUrl: string | null;
  batchId: string;
  batchName: string;
}

interface PredictionDetailNode {
  id: string;
  name: string;
  createdAt: string;
  modifiedAt: string;
  modelName: string;
  status: string;
  componentApiIds: Array<string>;
  configJson: Record<string, unknown> | null;
  hasResultsFile: boolean;
  resultsDownloadUrl: string | null;
  standardizedOutputFileName: string | null;
  standardizedOutputDownloadUrl: string | null;
  predictionBatch: {id: string; name: string} | null;
}

interface PredictionDetailQueryData {
  predictionJob: PredictionDetailNode | null;
}

export interface UsePredictionDetailResult {
  prediction: PredictionDetail | null;
  error: string | null;
  isLoading: boolean;
  refresh: () => void;
}

function mapToPredictionDetail(node: PredictionDetailNode): PredictionDetail {
  return {
    id: node.id,
    name: node.name,
    createdAt: node.createdAt ?? '',
    modifiedAt: node.modifiedAt ?? '',
    modelName: node.modelName ?? '',
    status: STATUS_CODE_TO_STATUS[node.status] ?? 'PENDING',
    componentApiIds: node.componentApiIds ?? [],
    configJson: node.configJson ?? {},
    hasResultsFile: node.hasResultsFile ?? false,
    resultsDownloadUrl: node.resultsDownloadUrl ?? null,
    standardizedOutputFileName: node.standardizedOutputFileName ?? null,
    standardizedOutputDownloadUrl: node.standardizedOutputDownloadUrl ?? null,
    batchId: node.predictionBatch?.id ?? '',
    batchName: node.predictionBatch?.name ?? '',
  };
}

export function usePredictionDetail(predictionId: string): UsePredictionDetailResult {
  const {data, loading, error, refetch} = useQuery<PredictionDetailQueryData>(PREDICTION_DETAIL_QUERY, {
    variables: {id: predictionId},
  });

  const node = data?.predictionJob ?? null;

  const refresh = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    prediction: node != null ? mapToPredictionDetail(node) : null,
    error: error != null ? error.message : null,
    isLoading: loading,
    refresh,
  };
}
